// Admin/research view for the booth corpus. Logged-in users only (the API
// gate is requireAccessToken; booth visitors have no token). Mirrors the
// survey raw-data download UX: a single click drops an .xlsx file.
//
// Also shows live aggregate stats so you can confirm fresh booth traffic
// without leaving the page.

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

const fetchAuthed = async (url, responseType = 'json') => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (responseType === 'blob') return { ok: res.ok, status: res.status, blob: await res.blob() };
  return { ok: res.ok, status: res.status, json: await res.json().catch(() => null) };
};

function StatTile({ label, value, sub }) {
  return (
    <div
      className="border rounded p-3 text-center"
      style={{ flex: '1 1 140px', minWidth: 0, background: '#fff' }}
    >
      <div className="small text-muted text-uppercase">{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{value}</div>
      {sub && <div className="small text-muted">{sub}</div>}
    </div>
  );
}

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sub: PropTypes.string,
};

function BoothAdminPage() {
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAuthError(false);
    const res = await fetchAuthed('/api/booth/sessions?limit=2000');
    if (res.status === 401 || res.status === 403 || res.status === 400) {
      setAuthError(true);
    } else if (!res.ok) {
      setError(`Failed to load (HTTP ${res.status})`);
    } else {
      setData(res.json);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadXlsx = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await fetchAuthed('/api/booth/sessions/download', 'blob');
      if (!res.ok) {
        setDownloadError(`Download failed (HTTP ${res.status})`);
        return;
      }
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booth_sessions_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setDownloadError(e?.message || 'Network error');
    } finally {
      setDownloading(false);
    }
  }, []);

  const downloadJson = useCallback(async () => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `booth_sessions_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, [data]);

  if (loading) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem' }}>
        <div className="text-muted">Loading booth corpus...</div>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem' }}>
        <h4>Booth corpus -- admin view</h4>
        <div className="alert alert-warning mt-3">
          You need to be logged in to view this page. Please
          {' '}
          <a href="/login">log in</a>
          {' '}
          and return here.
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem' }}>
        <h4>Booth corpus -- admin view</h4>
        <div className="alert alert-danger mt-3">{error}</div>
        <Button onClick={load} variant="outline-primary">Retry</Button>
      </div>
    );
  }

  const stats = data?.stats || {};
  const dailyCounters = data?.dailyCounters || [];
  const sessions = data?.sessions || [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayCounter = dailyCounters.find((d) => d.isoDate === todayIso);
  const recentRounds = [];
  sessions.slice(0, 25).forEach((s) => {
    (s.rounds || []).slice(-3).forEach((r) => recentRounds.push({ ...r, sessionId: s.sessionId }));
  });
  recentRounds.sort((a, b) => (new Date(b.ts).getTime()) - (new Date(a.ts).getTime()));
  const previewRounds = recentRounds.slice(0, 10);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '1.5rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Booth corpus -- admin view</h4>
        <Button size="sm" variant="outline-secondary" onClick={load}>
          Refresh
        </Button>
      </div>

      <div className="d-flex flex-wrap mb-3" style={{ gap: '0.5rem' }}>
        <StatTile
          label="Sessions"
          value={stats.sessionsReturned ?? 0}
          sub="visitor sessions"
        />
        <StatTile
          label="Total rounds"
          value={stats.totalRoundsInReturned ?? 0}
          sub="Submit clicks"
        />
        <StatTile
          label="Hostile"
          value={stats.hostileRoundsInReturned ?? 0}
          sub="Pass 1 = HOSTILE"
        />
        <StatTile
          label="Today's rounds"
          value={todayCounter ? todayCounter.dailyRoundsCount : 0}
          sub={todayIso}
        />
      </div>

      <div className="d-flex flex-wrap mb-4" style={{ gap: '0.5rem' }}>
        <Button onClick={downloadXlsx} disabled={downloading}>
          {downloading ? 'Generating...' : 'Download Excel (.xlsx)'}
        </Button>
        <Button variant="outline-primary" onClick={downloadJson} disabled={!data}>
          Download JSON
        </Button>
      </div>
      {downloadError && (
        <div className="alert alert-warning small py-2">{downloadError}</div>
      )}

      <h5 className="mt-4">Recent rounds</h5>
      <div className="small text-muted mb-2">
        Last 10 Submit clicks across the most recent 25 sessions. Excel
        export has the full corpus across three sheets (Summary, Sessions,
        Rounds).
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>When</th>
              <th>Comment</th>
              <th>Screen</th>
              <th>Quality</th>
              <th>Short-circuit</th>
            </tr>
          </thead>
          <tbody>
            {previewRounds.map((r) => (
              <tr key={`${r.sessionId}-${r.ts}`}>
                <td className="small text-nowrap">
                  {r.ts ? new Date(r.ts).toLocaleString() : ''}
                </td>
                <td style={{ maxWidth: 360 }}>
                  <div className="small" style={{ whiteSpace: 'pre-line' }}>
                    {(r.inputText || '').slice(0, 240)}
                    {(r.inputText || '').length > 240 ? '...' : ''}
                  </div>
                </td>
                <td className="small">
                  {r.isHostile ? (
                    <span className="badge bg-danger">{r.screenLabel || 'HOSTILE'}</span>
                  ) : (
                    <span className="badge bg-secondary">{r.screenLabel || '-'}</span>
                  )}
                </td>
                <td className="small">{r.qualityGateLabel || '-'}</td>
                <td className="small">{r.shortCircuit || '-'}</td>
              </tr>
            ))}
            {previewRounds.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted small">No rounds yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BoothAdminPage;
